import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaEdit, FaSave, FaTimes as FaCancel } from 'react-icons/fa';
import './db_task_popup.css';

const DbTaskPopup = ({ task, onClose, onSave, sourceOptions }) => {
  // # Initialize state for edited task and editing mode
  const [editedTask, setEditedTask] = useState(task);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  // # Reset edited task when original task changes
  useEffect(() => {
    setEditedTask(task);
  }, [task]);

  // # Handle input changes with validation
  const handleInputChange = (field, value) => {
    setEditedTask(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for the field being edited
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // # Validate form before saving
  const validateForm = () => {
    const newErrors = {};
    
    if (!editedTask.task_name?.trim()) {
      newErrors.task_name = 'Task name is required';
    }
    
    if (!editedTask.source) {
      newErrors.source = 'Source is required';
    }
    
    if (!editedTask.subchapter?.trim()) {
      newErrors.subchapter = 'Subchapter is required';
    }
    
    if (!editedTask.plan) {
      newErrors.plan = 'Plan is required';
    }
    
    if (!editedTask.day || editedTask.day < 1) {
      newErrors.day = 'Valid day is required';
    }
    
    if (!editedTask.duration || editedTask.duration < 1) {
      newErrors.duration = 'Valid duration is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // # Handle save with validation
  const handleSave = () => {
    if (validateForm()) {
      onSave(editedTask);
      setIsEditing(false);
    }
  };

  // # Handle escape key to close popup
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isEditing) {
          setEditedTask(task);
          setIsEditing(false);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isEditing, task, onClose]);

  return (
    <motion.div 
      className="mstr_db_task_popup_overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => e.target.className === 'mstr_db_task_popup_overlay' && onClose()}
    >
      <motion.div 
        className="mstr_db_task_popup_content"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mstr_db_task_popup_header">
          <motion.h3
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {isEditing ? 'Edit Task' : 'Task Details'}
          </motion.h3>
          <button className="mstr_db_task_popup_close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="mstr_db_task_popup_body">
          {/* Section 1: Task Identification */}
          <div className="mstr_db_task_popup_section identification">
            <h4 className="section-title">Task Identification</h4>
            <div className="mstr_db_task_popup_grid">
              <div className="mstr_db_task_popup_field">
                <label>Step</label>
                <input
                  type="text"
                  value={task.step}
                  disabled={true}
                  className="read-only"
                />
              </div>

              <div className="mstr_db_task_popup_field">
                <label>Chapter</label>
                <input
                  type="text"
                  value={task.chapter}
                  disabled={true}
                  className="read-only"
                />
              </div>

              <div className="mstr_db_task_popup_field">
                <label>Task ID</label>
                <input
                  type="text"
                  value={task.task_id}
                  disabled={true}
                  className="read-only"
                />
              </div>

              <div className="mstr_db_task_popup_field">
                <label>Subchapter {errors.subchapter && <span className="error">*</span>}</label>
                <input
                  type="text"
                  value={isEditing ? editedTask.subchapter : task.subchapter}
                  onChange={(e) => handleInputChange('subchapter', e.target.value)}
                  disabled={!isEditing}
                  className={errors.subchapter ? 'error' : ''}
                />
                {errors.subchapter && <span className="error-message">{errors.subchapter}</span>}
              </div>
            </div>
          </div>

          {/* Section 2: Task Details */}
          <div className="mstr_db_task_popup_section details">
            <h4 className="section-title">Task Details</h4>
            <div className="mstr_db_task_popup_grid">
              <div className="mstr_db_task_popup_field full-width">
                <label>Task Name {errors.task_name && <span className="error">*</span>}</label>
                <input
                  type="text"
                  value={isEditing ? editedTask.task_name : task.task_name}
                  onChange={(e) => handleInputChange('task_name', e.target.value)}
                  disabled={!isEditing}
                  className={errors.task_name ? 'error' : ''}
                />
                {errors.task_name && <span className="error-message">{errors.task_name}</span>}
              </div>

              <div className="mstr_db_task_popup_field">
                <label>Source {errors.source && <span className="error">*</span>}</label>
                <select
                  value={isEditing ? editedTask.source : task.source}
                  onChange={(e) => handleInputChange('source', e.target.value)}
                  disabled={!isEditing}
                  className={errors.source ? 'error' : ''}
                >
                  <option value="">Select Source...</option>
                  {sourceOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.source && <span className="error-message">{errors.source}</span>}
              </div>

              <div className="mstr_db_task_popup_field">
                <label>Plan {errors.plan && <span className="error">*</span>}</label>
                <select
                  value={isEditing ? editedTask.plan : task.plan}
                  onChange={(e) => handleInputChange('plan', e.target.value)}
                  disabled={!isEditing}
                  className={errors.plan ? 'error' : ''}
                >
                  <option value="">Select Plan...</option>
                  <option value="Plan A">Plan A</option>
                  <option value="Plan B">Plan B</option>
                  <option value="Plan C">Plan C</option>
                </select>
                {errors.plan && <span className="error-message">{errors.plan}</span>}
              </div>

              <div className="mstr_db_task_popup_field">
                <label>Day {errors.day && <span className="error">*</span>}</label>
                <input
                  type="number"
                  min="1"
                  value={isEditing ? editedTask.day : task.day}
                  onChange={(e) => handleInputChange('day', e.target.value)}
                  disabled={!isEditing}
                  className={errors.day ? 'error' : ''}
                />
                {errors.day && <span className="error-message">{errors.day}</span>}
              </div>

              <div className="mstr_db_task_popup_field">
                <label>Duration (min) {errors.duration && <span className="error">*</span>}</label>
                <input
                  type="number"
                  min="1"
                  value={isEditing ? editedTask.duration : task.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  disabled={!isEditing}
                  className={errors.duration ? 'error' : ''}
                />
                {errors.duration && <span className="error-message">{errors.duration}</span>}
              </div>

              <div className="mstr_db_task_popup_field">
                <label>Type</label>
                <input
                  type="text"
                  value={isEditing ? editedTask.type : task.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  disabled={!isEditing}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mstr_db_task_popup_footer">
          {isEditing ? (
            <>
              <button 
                className="mstr_db_task_popup_button cancel"
                onClick={() => {
                  setEditedTask(task);
                  setIsEditing(false);
                  setErrors({});
                }}
              >
                <FaCancel /> Cancel
              </button>
              <button 
                className="mstr_db_task_popup_button save"
                onClick={handleSave}
              >
                <FaSave /> Save
              </button>
            </>
          ) : (
            <button 
              className="mstr_db_task_popup_button edit"
              onClick={() => setIsEditing(true)}
            >
              <FaEdit /> Edit
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DbTaskPopup;
